import React, { useEffect, useState } from 'react';
import {
  Alert,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  TextField,
} from '@mui/material';
import Button from '@components/Button';
import UserService from '@api/userService';
import getApiErrorMessage from '@utils/getApiErrorMessage';
import { toast } from 'react-toastify';

type Props = {
  open: boolean;
  userId?: number;
  currentEmail: string;
  userName: string;
  onClose: () => void;
  onChanged: (email: string) => void;
};

function ChangeSignInEmailDialog({
  open,
  userId,
  currentEmail,
  userName,
  onClose,
  onChanged,
}: Props) {
  const [email, setEmail] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setEmail('');
      setConfirmed(false);
      setError('');
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!userId) return;

    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail || !/^\S+@\S+\.\S+$/.test(normalizedEmail)) {
      setError('Enter a valid email address.');
      return;
    }
    if (normalizedEmail === currentEmail.trim().toLowerCase()) {
      setError('The new email must be different from the current email.');
      return;
    }
    if (!confirmed) {
      setError('Confirm that the new email belongs to the same person.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await UserService.changeSignInEmail(userId, {
        email: normalizedEmail,
        confirmSamePerson: true,
      });
      onChanged(response.data.email);
      onClose();
      toast.success(
        'Sign-in email updated. The user must redeem the new invitation before signing in with it.',
      );
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'Unable to change the sign-in email.'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitClick = () => {
    handleSubmit().catch(() => undefined);
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Change sign-in email</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          This keeps the same user identity, permissions, facilities, and report assignments. Use it
          only when the new address belongs to
          {' '}
          {userName || 'the same person'}
          . Account transfers require a new user.
        </DialogContentText>
        <TextField
          label="Current sign-in email"
          value={currentEmail}
          fullWidth
          disabled
          margin="dense"
        />
        <TextField
          label="New sign-in email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          fullWidth
          required
          autoFocus
          margin="dense"
          disabled={loading}
        />
        <FormControlLabel
          sx={{ mt: 1, alignItems: 'flex-start' }}
          control={(
            <Checkbox
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
              disabled={loading}
            />
          )}
          label="I confirm that this is the same person and that this is not an account transfer."
        />
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmitClick}
          loading={loading}
          disabled={!confirmed || !email.trim()}
        >
          Change email
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ChangeSignInEmailDialog;
